import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { track } from "@cakewalk/frontend-api";
import {
  ArrowRight,
  CalendarDays,
  ClipboardList,
  Clock,
  MapPin,
  User,
  UserRound,
  Users,
} from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2Checkbox,
  V2EnrollmentFlowMain,
  V2FormActions,
  V2Typography,
} from "@cakewalk/ui/v2";
import type { ComponentType } from "react";
import { useId, useState } from "react";

/** The four small facts we collect per person — rendered as scannable chips. */
const DATA_POINTS: { icon: ComponentType<{ className?: string }>; label: string }[] = [
  { icon: User, label: "Name" },
  { icon: CalendarDays, label: "Date of birth" },
  { icon: UserRound, label: "Gender" },
  { icon: MapPin, label: "ZIP code" },
];

/** How the flow works — one scannable, icon-led line each (no paragraphs). */
const HOW_IT_WORKS: { icon: ComponentType<{ className?: string }>; text: string }[] = [
  { icon: User, text: "Don't forget to add yourself and your own dependents!" },
  {
    icon: Users,
    text: "Add your team members one at a time, and each employee's dependents before moving on to the next.",
  },
  { icon: Clock, text: "The whole process should only take a few minutes." },
];

/**
 * Intro step of the company-census flow — shown right after login / continue as
 * guest, before the business step. Sets expectations: a short roster of everyone
 * to cover (employees + dependents) with name, DOB, gender, and ZIP. Mirrors the
 * "Company census" intro prototype. Mostly informational, but the client must
 * authorize us to submit the census to carriers before "Get started" advances
 * to the business step. As the first real step (login/signup are pre-auth),
 * there is no Back affordance.
 */
export function CompanyCensusIntroStep({
  playBuild = false,
  ceremony = null,
  onStart,
}: {
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onStart: () => void;
}) {
  const [authorized, setAuthorized] = useState(false);
  const authorizeId = useId();

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  return (
    <V2EnrollmentFlowMain width="default">
      <div className="mx-auto w-full max-w-cw-v2-content-narrow pb-8">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <V2Typography
            variant="overline"
            {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
          >
            Team &amp; coverage
          </V2Typography>
          <V2Typography variant="display" as="h1" {...mergeStepBuildProps(titleMotion)}>
            Let&rsquo;s gather your details
          </V2Typography>
          <V2Typography
            variant="body-large"
            muted
            {...mergeStepBuildProps(descriptionMotion, { className: "max-w-xl" })}
          >
            Tell us about your business and everyone you&rsquo;d like covered. We use this to build
            accurate quotes for your team.
          </V2Typography>
        </div>

        <V2Card
          {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony), {
            className: "overflow-hidden",
          })}
        >
          <V2CardContent className="flex flex-col gap-5 p-6 sm:p-8">
            {/* Icon + everything it introduces share one column; the icon sits
                beside the title and the body content lines up under the title
                text (icon column = h-10 + gap-3 = 52px). */}
            <div className="flex items-start gap-3">
              {/* Decorative — hidden on mobile to reclaim horizontal space. */}
              <span
                aria-hidden
                className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cw-v2-accent/10 text-cw-v2-accent sm:flex"
              >
                <ClipboardList className="h-5 w-5" />
              </span>
              <div className="flex flex-1 flex-col gap-5">
                {/* Title + its lead-in + chips are one unit. Title→lead-in is
                    near-flush (2px); lead-in→chips a touch more (8px). */}
                <div className="flex flex-col gap-2">
                  {/* Title sits all but flush to the line it heads. */}
                  <div className="flex flex-col gap-0.5">
                    <V2Typography variant="heading-small" as="h2" className="pt-1.5 font-semibold">
                      What we need from you
                    </V2Typography>
                    <V2Typography variant="body" muted>
                      For each person — employees and any dependents (spouse or children) —
                      you&rsquo;ll enter:
                    </V2Typography>
                  </div>
                  {/* The four data points as scannable chips — "that's all we ask" at a glance. */}
                  <ul className="flex flex-wrap gap-2">
                    {DATA_POINTS.map(({ icon: Icon, label }) => (
                      <li
                        key={label}
                        className="flex items-center gap-2 rounded-full border border-cw-v2-border bg-cw-v2-sidewalk/40 py-1.5 pr-4 pl-3"
                      >
                        <Icon className="h-4 w-4 text-cw-v2-accent" />
                        <V2Typography
                          variant="meta"
                          as="span"
                          className="font-semibold text-cw-v2-ink"
                        >
                          {label}
                        </V2Typography>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="h-px w-full bg-cw-v2-border/60" />

                {/* How it works — one icon-led line each, scannable in a glance. */}
                <ul className="flex flex-col gap-3">
                  {HOW_IT_WORKS.map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-start gap-3">
                      <span
                        aria-hidden
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cw-v2-accent/10 text-cw-v2-accent"
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <V2Typography variant="body" className="pt-0.5 text-cw-v2-ink">
                        {text}
                      </V2Typography>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="h-px w-full bg-cw-v2-border/60" />

            {/* Consent lives inside the card as a light confirmation row — not a
                walled-off legal gate. Still gates "Get started" (§4 forms rule). */}
            <div className="flex flex-col gap-2">
              <V2Typography variant="body" className="text-cw-v2-ink">
                <span className="font-semibold">One more thing</span> — we need your permission to
                solicit quotes for you.
              </V2Typography>
              <label
                htmlFor={authorizeId}
                className="flex cursor-pointer items-start gap-3 text-left"
              >
                <V2Checkbox
                  id={authorizeId}
                  checked={authorized}
                  onCheckedChange={(next) => {
                    // tracking-plan: Flow 11 (company_census) > census_authorize_toggled (P2)
                    track("census_authorize_toggled", {
                      flow_name: "company_census",
                      step_name: "intro",
                      authorized: next === true,
                    });
                    setAuthorized(next === true);
                  }}
                  className="mt-0.5"
                  data-track="checkbox-company-census-authorize"
                />
                <V2Typography variant="meta" as="span" muted>
                  I authorize Cakewalk to submit this census to insurance carriers for the purpose
                  of obtaining health insurance quotes for my company.
                </V2Typography>
              </label>
            </div>
          </V2CardContent>
        </V2Card>

        <V2FormActions variant="registration" className="mt-8">
          <V2Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={!authorized}
            onClick={onStart}
            iconSuffix={<ArrowRight className="h-4 w-4" />}
            data-track="button-company-census-intro-start"
          >
            Get started
          </V2Button>
        </V2FormActions>
      </div>
    </V2EnrollmentFlowMain>
  );
}
